import React, { useState } from "react";
import { useToast } from "@/shared/components/ui/use-toast";
import * as engineService from "@/shared/api/services/engineService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Eye, Power, PowerOff, Users,
  CheckCircle2, XCircle, Clock, Building2, Loader2, TrendingUp,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import EngineResellerForm from "@/shared/components/engine/EngineResellerForm";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "text-emerald-400", bg: "rgba(52,211,153,0.1)", border: "rgba(52,211,153,0.25)", label: "Active" },
  active: { icon: CheckCircle2, color: "text-emerald-400", bg: "rgba(52,211,153,0.1)", border: "rgba(52,211,153,0.25)", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "text-red-400", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", label: "Suspended" },
  suspended: { icon: XCircle, color: "text-red-400", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.25)", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "text-amber-400", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.25)", label: "Inactive" },
  pending: { icon: Clock, color: "text-amber-400", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.25)", label: "Pending" },
};

/* ─── Stat Card ─────────────────────────────────────────────── */
function StatCard({ title, value, icon: Icon, gradient, accent, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 180, damping: 18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
      style={{ perspective: 900 }}
    >
      <motion.div
        animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1.06 : 0.85 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${accent}1a, transparent 68%)`, filter: "blur(8px)" }}
      />
      <motion.div
        animate={{ y: hovered ? -6 : 0, rotateX: hovered ? -1.5 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 24 }}
        className="relative rounded-2xl overflow-hidden p-5"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
          border: `1px solid ${hovered ? `${accent}35` : "rgba(148,163,184,0.1)"}`,
          boxShadow: hovered ? `0 18px 50px rgba(0,0,0,0.5), 0 0 28px ${accent}18` : "0 4px 18px rgba(0,0,0,0.3)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}50, transparent)` }} />
        <motion.div
          animate={{ x: hovered ? "108%" : "-108%" }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="absolute top-0 right-0 w-24 h-24 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${accent}08, transparent 70%)` }} />

        <div className="relative flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
            <motion.p animate={{ scale: hovered ? 1.04 : 1 }} className="text-2xl font-bold text-slate-100">{value}</motion.p>
          </div>
          <motion.div
            animate={{ scale: hovered ? 1.12 : 1, rotate: hovered ? 6 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 18 }}
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${gradient})`,
              boxShadow: hovered ? `0 0 22px ${accent}44` : `0 4px 14px ${accent}28`,
              transition: "box-shadow 0.3s",
            }}
          >
            <Icon className="h-5 w-5 text-white" />
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineResellers() {
  const [search, setSearch] = useState("");
  const [statusDialog, setStatusDialog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingReseller, setEditingReseller] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();
  const resellerName = "Gaming Partners Ltd";

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  const { data: resellersData = { data: [], total: 0, totalPages: 0 }, isLoading } = useQuery({
    queryKey: ["engine-resellers", page, limit, search],
    queryFn: () => engineService.getMyResellers(page, limit, search),
    keepPreviousData: true,
  });

  const { data: stats = { totalResellers: 0, activeResellers: 0, totalMerchants: 0 } } = useQuery({
    queryKey: ["engine-dashboard-stats"],
    queryFn: () => engineService.getDashboardStats(),
  });

  const resellers = resellersData.data || [];
  const totalItems = resellersData.total || 0;
  const totalPages = resellersData.totalPages || 0;

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => engineService.updateReseller(id, data),
    onSuccess: () => { queryClient.invalidateQueries(["engine-resellers"]); setStatusDialog(null); },
  });

  const createMutation = useMutation({
    mutationFn: (data) => engineService.createReseller(data),
    onSuccess: () => { queryClient.invalidateQueries(["engine-resellers"]); setShowForm(false); setEditingReseller(null); setSaving(false); },
  });

  const handleSaveReseller = async (data) => {
    setSaving(true);
    try {
      if (editingReseller) {
        await updateMutation.mutateAsync({ id: editingReseller.id, data: { ...data } });
        toast({
          title: t("resellerUpdated") || "Reseller Updated",
          description: `${t("reseller") || "Reseller"} ${data.name} ${t("hasBeenUpdated") || "has been successfully updated."}`,
        });
      } else {
        await createMutation.mutateAsync(data);
        toast({
          title: t("resellerCreated") || "Reseller Created",
          description: `${t("reseller") || "Reseller"} ${data.name} ${t("hasBeenCreated") || "has been successfully created."}`,
        });
      }
      setShowForm(false);
      setEditingReseller(null);
    } catch (e) {
      toast({
        variant: "destructive",
        title: t("actionFailed") || "Action Failed",
        description: e.response?.data?.message || e.message || t("somethingWentWrong") || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (e) => {
    if (e) e.preventDefault();
    if (!statusDialog) return;
    try {
      const newStatus = statusDialog.status?.toUpperCase() === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
      await updateMutation.mutateAsync({ id: statusDialog.id, data: { name: statusDialog.name, status: newStatus } });
      toast({
        title: t("statusUpdated") || "Status Updated",
        description: `${t("reseller") || "Reseller"} ${statusDialog.name} ${t("isNow") || "is now"} ${newStatus.toLowerCase()}.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("updateFailed") || "Update Failed",
        description: err.message || t("somethingWentWrong") || "Something went wrong.",
      });
    }
  };

  const filteredResellers = resellers; // Server-side filtering
  const activeResellersCount = stats.activeResellers || 0;
  const globalTotalResellers = stats.totalResellers || 0;
  const globalTotalMerchants = stats.totalMerchants || 0;

  return (
    <EngineLayout currentPage="EngineResellers" resellerName={resellerName}>
      <div className="space-y-6 w-full max-w-full">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
            border: "1px solid rgba(167,139,250,0.15)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.5), rgba(244,114,182,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(167,139,250,0.07), transparent 70%)" }} />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-violet-400 mb-1">Partner Management</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("resellers")}</h1>
              <p className="text-sm text-slate-400">
                <span className="text-violet-400 font-semibold">{globalTotalResellers}</span> {t("partnersCount")}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => { setEditingReseller(null); setIsViewOnly(false); setShowForm(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer outline-none border-0"
              style={{
                background: "linear-gradient(135deg, #a78bfa, #7c3aed)",
                boxShadow: "0 4px 20px rgba(167,139,250,0.38)",
                transition: "box-shadow 0.2s",
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 32px rgba(167,139,250,0.55)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(167,139,250,0.38)"}
            >
              <Plus className="h-4 w-4" />
              {t("addReseller")}
            </motion.button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard title={t("totalResellers")} value={globalTotalResellers} icon={Users} gradient="#a78bfa, #7c3aed" accent="#a78bfa" delay={0} />
          <StatCard title={t("activeResellers")} value={activeResellersCount} icon={CheckCircle2} gradient="#34d399, #059669" accent="#34d399" delay={0.05} />
          <StatCard title={t("totalMerchants")} value={globalTotalMerchants} icon={Building2} gradient="#22d3ee, #0284c7" accent="#22d3ee" delay={0.1} />
        </div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="relative max-w-sm"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t("search")}
            className="w-full h-10 pl-9 pr-4 rounded-xl text-sm text-slate-200 placeholder:text-slate-600 outline-none"
            style={{
              background: "rgba(15,23,42,0.7)",
              border: "1px solid rgba(148,163,184,0.12)",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}
            onFocus={e => { e.target.style.borderColor = "rgba(167,139,250,0.3)"; e.target.style.boxShadow = "0 0 0 3px rgba(167,139,250,0.07)"; }}
            onBlur={e => { e.target.style.borderColor = "rgba(148,163,184,0.12)"; e.target.style.boxShadow = "none"; }}
          />
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
            border: "1px solid rgba(148,163,184,0.09)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.3), transparent)" }} />

          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 580 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.08)", background: "rgba(2,6,23,0.4)" }}>
                  {[t("reseller"), t("code"), t("status"), t("merchants"), t("created"), ""].map((h, i) => (
                    <th key={i} className="text-left px-4 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="wait">
                  {isLoading ? (
                    <tr><td colSpan={6} className="text-center py-14">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Loader2 className="h-5 w-5 text-violet-400 animate-spin" />
                        <span className="text-sm">{t("loading")}</span>
                      </div>
                    </td></tr>
                  ) : filteredResellers.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-14 text-slate-600 text-sm">{t("noResellers")}</td></tr>
                  ) : filteredResellers.map((r, i) => {
                    const st = statusConfig[r.status] || statusConfig.pending;
                    const StatusIcon = st.icon;
                    return (
                      <motion.tr
                        key={r.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ delay: i * 0.03 }}
                        style={{ borderBottom: "1px solid rgba(148,163,184,0.05)", transition: "background 0.18s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(167,139,250,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <motion.div
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: "linear-gradient(135deg, rgba(167,139,250,0.2), rgba(124,58,237,0.1))", border: "1px solid rgba(167,139,250,0.2)", transition: "box-shadow 0.25s" }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 14px rgba(167,139,250,0.3)"}
                              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                            >
                              <Users className="h-4 w-4 text-violet-400" />
                            </motion.div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-200 truncate">{r.name}</p>
                              <p className="text-xs text-slate-500 truncate">{r.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="font-mono text-xs text-slate-400 px-2.5 py-1 rounded-lg" style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.12)" }}>
                            {r.code}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: st.bg, border: `1px solid ${st.border}` }}>
                            <StatusIcon className={`h-3 w-3 ${st.color}`} />
                            <span className={st.color}>{st.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                            <Building2 className="h-3.5 w-3.5 text-slate-600" />
                            <span>{r.merchantsCount || 0}</span>
                            <span className="text-slate-600">/ {r.maxMerchants || 10}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-slate-500">
                          {r.createdAt ? format(new Date(r.createdAt), "MMM d, yyyy") : "N/A"}
                        </td>
                        <td className="px-4 py-3.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer outline-none"
                                style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.12)", transition: "background 0.18s" }}
                                onMouseEnter={e => e.currentTarget.style.background = "rgba(167,139,250,0.12)"}
                                onMouseLeave={e => e.currentTarget.style.background = "rgba(167,139,250,0.06)"}
                              >
                                <MoreHorizontal className="h-4 w-4 text-slate-500" />
                              </motion.button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" style={{ background: "rgba(2,6,23,0.97)", border: "1px solid rgba(167,139,250,0.12)", borderRadius: 12, padding: 4, minWidth: 156 }}>
                              <DropdownMenuItem onClick={() => { setEditingReseller(r); setIsViewOnly(true); setShowForm(true); }} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800 text-slate-400">
                                <Eye className="h-3.5 w-3.5 mr-2 text-violet-400" />{t("view")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setEditingReseller(r); setIsViewOnly(false); setShowForm(true); }} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800 text-slate-400">
                                <Pencil className="h-3.5 w-3.5 mr-2 text-cyan-400" />{t("edit")}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator style={{ background: "rgba(167,139,250,0.08)", margin: "4px 0" }} />
                              <DropdownMenuItem onClick={() => setStatusDialog(r)} className="text-sm rounded-lg cursor-pointer hover:bg-slate-800" style={{ color: r.status?.toUpperCase() === "ACTIVE" ? "#f87171" : "#34d399" }}>
                                {r.status?.toUpperCase() === "ACTIVE"
                                  ? <><PowerOff className="h-3.5 w-3.5 mr-2" />{t("suspend")}</>
                                  : <><Power className="h-3.5 w-3.5 mr-2" />{t("activate")}</>}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800/50 bg-slate-900/30">
              <div className="text-xs text-slate-500">
                {t("showing")} <span className="text-slate-300">{(page - 1) * limit + 1}</span> - <span className="text-slate-300">{Math.min(page * limit, totalItems)}</span> {t("of")} <span className="text-slate-300">{totalItems}</span>
              </div>
              <div className="flex items-center gap-2">
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-800/50 border border-slate-700/50 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </motion.button>
                <div className="flex items-center gap-1.5 mx-2">
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i} onClick={() => setPage(i + 1)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${page === i + 1 ? "bg-violet-500 text-white shadow-lg shadow-violet-500/20" : "bg-slate-800/40 text-slate-500 hover:bg-slate-800"}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-800/50 border border-slate-700/50 text-slate-400 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Status dialog */}
      <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
        <AlertDialogContent style={{ background: "rgba(2,6,23,0.98)", border: "1px solid rgba(167,139,250,0.14)", borderRadius: 16 }}>
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(167,139,250,0.4), transparent)" }} />
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">{statusDialog?.status?.toUpperCase() === "ACTIVE" ? t("suspendReseller") : t("activateReseller")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {statusDialog?.status?.toUpperCase() === "ACTIVE"
                ? `${t("suspendMerchantConfirm")} "${statusDialog?.name}"?`
                : `${t("activateMerchantConfirm")} "${statusDialog?.name}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.14)", color: "#94a3b8", borderRadius: 10 }}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleToggleStatus}
              disabled={updateMutation.isPending}
              className="flex items-center gap-2"
              style={{
                borderRadius: 10, border: "none", fontWeight: 700, cursor: "pointer",
                background: statusDialog?.status?.toUpperCase() === "ACTIVE" ? "linear-gradient(135deg, #ef4444, #dc2626)" : "linear-gradient(135deg, #34d399, #059669)",
                color: "#fff",
                minWidth: 100,
                opacity: updateMutation.isPending ? 0.7 : 1,
              }}
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                statusDialog?.status?.toUpperCase() === "ACTIVE" ? t("suspend") : t("activate")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EngineResellerForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditingReseller(null); setIsViewOnly(false); }}
        reseller={editingReseller}
        onSave={handleSaveReseller}
        saving={saving}
        readOnly={isViewOnly}
      />
    </EngineLayout>
  );
}